import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { fromZonedTime } from "date-fns-tz";

/** The `{ start, end, label }` object written to a date filter URL key. */
export type DateRangeValue = { start: string; end: string; label: string };

/**
 * A selectable quick-range preset. `build` reads the clock when the row is
 * clicked (so the range is always relative to "now") and resolves day
 * boundaries in the viewer's timezone — unlike the office presets, which pin
 * everything to a single hardcoded office zone.
 */
export type DatePreset = {
  label: string;
  build: (timeZone: string) => DateRangeValue;
};

/** Sentinel label for the custom-range row (opens the date picker). */
export const CUSTOM_DATE_LABEL = "Custom...";

const zonedStart = (date: Date, timeZone: string) =>
  fromZonedTime(startOfDay(date), timeZone).toISOString();
const zonedEnd = (date: Date, timeZone: string) =>
  fromZonedTime(endOfDay(date), timeZone).toISOString();

/** A single calendar day [00:00, 23:59:59.999] in the viewer timezone. */
const day = (label: string, date: Date, timeZone: string): DateRangeValue => ({
  start: zonedStart(date, timeZone),
  end: zonedEnd(date, timeZone),
  label,
});

/** A span from the start-of-day of `from` to the end-of-day of `to`. */
const span = (
  label: string,
  from: Date,
  to: Date,
  timeZone: string,
): DateRangeValue => ({
  start: zonedStart(from, timeZone),
  end: zonedEnd(to, timeZone),
  label,
});

const today: DatePreset = {
  label: "Today",
  build: (tz) => day("Today", new Date(), tz),
};
const yesterday: DatePreset = {
  label: "Yesterday",
  build: (tz) => day("Yesterday", subDays(new Date(), 1), tz),
};
const thisWeek: DatePreset = {
  label: "This week",
  build: (tz) => {
    const now = new Date();
    return span("This week", startOfWeek(now), endOfWeek(now), tz);
  },
};
const lastWeek: DatePreset = {
  label: "Last week",
  build: (tz) => {
    const ref = subWeeks(new Date(), 1);
    return span("Last week", startOfWeek(ref), endOfWeek(ref), tz);
  },
};
const thisMonth: DatePreset = {
  label: "This month",
  build: (tz) => {
    const now = new Date();
    return span("This month", startOfMonth(now), endOfMonth(now), tz);
  },
};
const lastMonth: DatePreset = {
  label: "Last month",
  build: (tz) => {
    const ref = subMonths(new Date(), 1);
    return span("Last month", startOfMonth(ref), endOfMonth(ref), tz);
  },
};
const tomorrow: DatePreset = {
  label: "Tomorrow",
  build: (tz) => day("Tomorrow", addDays(new Date(), 1), tz),
};
const nextWeek: DatePreset = {
  label: "Next week",
  build: (tz) => {
    const ref = addWeeks(new Date(), 1);
    return span("Next week", startOfWeek(ref), endOfWeek(ref), tz);
  },
};
const nextMonth: DatePreset = {
  label: "Next month",
  build: (tz) => {
    const ref = addMonths(new Date(), 1);
    return span("Next month", startOfMonth(ref), endOfMonth(ref), tz);
  },
};
const custom: DatePreset = {
  label: CUSTOM_DATE_LABEL,
  build: () => ({ start: "", end: "", label: CUSTOM_DATE_LABEL }),
};

/**
 * Calendar presets for a record's past date (an invoice's date, an upload
 * date) — backward-looking only, no forward (tomorrow/next) options.
 */
export const DATE_PRESETS: DatePreset[] = [
  today,
  yesterday,
  thisWeek,
  lastWeek,
  thisMonth,
  lastMonth,
  custom,
];

/**
 * Forward-looking presets for a date that is always ahead (the invoices' Next
 * payment, the next autopay charge) — the quick ranges point ahead
 * (tomorrow/next) instead of behind.
 */
export const UPCOMING_DATE_PRESETS: DatePreset[] = [
  today,
  tomorrow,
  thisWeek,
  nextWeek,
  thisMonth,
  nextMonth,
  custom,
];
